/**
 * Global guard: when user has token and accountStatus === 'pending_deletion',
 * block access and show ReactivationGate before they can use any tier.
 * X-style: on sign-in during grace period, user sees reactivation screen first — never app content.
 */

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { apiClient } from '../api/client';
import { API_ENDPOINTS } from '../api/endpoints';
import { ReactivationGate } from './ReactivationGate';
import { AppLoadingSpinner } from './AppLoadingSpinner';
import { isStoredAuthTokenJwt } from '../api/client';
import { cognitoAuthService } from '../services/cognitoAuth.service';

export function PendingDeletionGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [pendingInfo, setPendingInfo] = useState<{ tier: string; scheduledDeletionAt?: string } | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!isStoredAuthTokenJwt()) {
      setChecked(true);
      setPendingInfo(null);
      return;
    }
    let cancelled = false;
    apiClient
      .get<{ accountStatus?: string; subscriptionTier?: string; tier?: string; scheduledDeletionAt?: string }>(
        API_ENDPOINTS.AUTH.ME,
        { silent401: true }
      )
      .then((data) => {
        if (cancelled) return;
        if (data?.accountStatus === 'pending_deletion') {
          const tier = data?.subscriptionTier || data?.tier || 'starter';
          setPendingInfo({
            tier,
            scheduledDeletionAt: data?.scheduledDeletionAt,
          });
        } else {
          setPendingInfo(null);
        }
      })
      .catch(() => {
        if (!cancelled) setPendingInfo(null);
      })
      .finally(() => {
        if (!cancelled) setChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await cognitoAuthService.signOut();
      if (typeof localStorage !== 'undefined') localStorage.removeItem('authToken');
      window.location.href = '/';
    } catch {
      if (typeof localStorage !== 'undefined') localStorage.removeItem('authToken');
      window.location.href = '/';
    }
  };

  // X-style: while checking with token, block app content — show loading so user never sees tier before reactivation gate
  if (!checked && isStoredAuthTokenJwt()) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
        <AppLoadingSpinner />
      </div>
    );
  }
  if (!checked) return <>{children}</>;
  if (pendingInfo) {
    return (
      <ReactivationGate
        tier={pendingInfo.tier}
        scheduledDeletionAt={pendingInfo.scheduledDeletionAt}
        onReactivated={() => {
          setPendingInfo(null);
          window.dispatchEvent(new CustomEvent('spinora-refetch-user'));
          window.location.reload();
        }}
        onLogout={handleLogout}
      />
    );
  }
  return <>{children}</>;
}
